import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import logo from '../assets/logo.jpeg';
import { theme } from '../theme';

const HeaderContainer = styled.header<{ isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${props => props.isScrolled
    ? 'rgba(61, 54, 53, 0.98)'
    : 'rgba(61, 54, 53, 0.85)'};
  box-shadow: ${props => props.isScrolled
    ? theme.shadows.medium
    : 'none'};
  z-index: 1000;
  transition: all 0.4s ease;
  border-bottom: ${props => props.isScrolled
    ? `1px solid ${theme.colors.burgundy}`
    : 'none'};
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 25px;
  width: 100%;
  max-width: 1300px;
`;

const Logo = styled.img`
  max-height: 85px;
  border-radius: ${theme.borderRadius.small};
  box-shadow: ${theme.shadows.small};
  transition: transform 0.3s ease, filter 0.3s ease;
  filter: brightness(1.05) contrast(1.05);
  
  &:hover {
    transform: scale(1.03);
    filter: brightness(1.1) contrast(1.1);
  }
`;

const Navigation = styled.nav`
  display: flex;
  gap: 38px;
  
  @media (max-width: 1023px) {
    display: none;
  }
`;

const NavLink = styled.a`
  font-family: ${theme.fonts.accent};
  font-weight: 500;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${theme.colors.light};
  text-decoration: none;
  position: relative;
  transition: all 0.3s ease;
  padding: 5px 0;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background-color: ${theme.colors.caramel};
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
  
  &:hover {
    color: ${theme.colors.caramel};
  }
  
  &.active {
    font-weight: 600;
    color: ${theme.colors.caramel};
    
    &::after {
      width: 100%;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: ${theme.colors.light};
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${theme.colors.caramel};
  }
  
  @media (max-width: 1023px) {
    display: block;
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  width: 100%;
  max-width: 1300px;
  flex-direction: column;
  align-items: center;
  padding: 10px 0 25px;
  background-color: rgba(61, 54, 53, 0.95);
  box-shadow: ${theme.shadows.medium};
  border-top: 1px solid rgba(200, 142, 23, 0.15);
  
  a {
    margin: 12px 0;
    font-family: ${theme.fonts.accent};
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${theme.colors.light};
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: ${theme.colors.caramel};
    }
  }
`;

type HeaderProps = {
  // Additional props can be added here if needed
};

const Header: React.FC<HeaderProps> = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <HeaderContainer isScrolled={isScrolled}>
      <HeaderContent>
        <Logo src={logo} alt="Fiori Italian Bistro" />
        <Navigation>
          <NavLink href="#home">Home</NavLink>
          <NavLink href="#menu">Menu</NavLink>
          <NavLink href="#gallery">Gallery</NavLink>
          <NavLink href="#contact">Contact</NavLink>
        </Navigation>
        <MobileMenuButton onClick={toggleMobileMenu}>
          {mobileMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
      </HeaderContent>
      <MobileMenu isOpen={mobileMenuOpen}>
        <NavLink href="#home">Home</NavLink>
        <NavLink href="#menu">Menu</NavLink>
        <NavLink href="#gallery">Gallery</NavLink>
        <NavLink href="#contact">Contact</NavLink>
      </MobileMenu>
    </HeaderContainer>
  );
};

export default Header; 